// src/components/core/entity/EntityChannelsCard/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Card, Button, Tag, Tooltip, Space, Popconfirm, Dropdown, Flex } from 'antd';
import {
  PlusOutlined,
  PhoneOutlined,
  UserOutlined,
  StarFilled,
  StarOutlined,
  WhatsAppOutlined,
  EditOutlined,
  RestOutlined,
  MailOutlined,
  AimOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import entitiesService from 'services/entitiesService';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { openModal } from 'components/core/GenericModal';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import ContactChannelModal from './ContactsChannelModal';
import ContactPersonModal from './ContactPersonModal';
import EditPersonModal from './ContactPersonModal/EditModal';
import { parsePhoneToDisplay, generateMongoObjectId } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CHANNEL_TYPE_CONFIG = {
  person: { color: 'purple', label: 'person', group: 'person' },
  website: { color: 'purple', label: 'website', group: 'website' },
  email: { color: 'green', label: 'email', group: 'email' },
  phone: { color: 'blue', label: 'phone', group: 'phone' },
  facebook: { color: 'geekblue', label: 'Facebook', group: 'social' },
  linkedin: { color: 'blue', label: 'LinkedIn', group: 'social' },
  instagram: { color: 'magenta', label: 'Instagram', group: 'social' },
  twitter: { color: 'cyan', label: 'Twitter / X', group: 'social' },
  youtube: { color: 'red', label: 'YouTube', group: 'social' },
  tiktok: { color: 'volcano', label: 'TikTok', group: 'social' },
  other: { color: 'default', label: 'other', group: 'other' },
};

// Sort order for channel groups (used for sorting)
const CHANNEL_GROUP_ORDER = {
  person: 0,
  website: 1,
  email: 2,
  phone: 3,
  social: 4,
  other: 5,
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EntityChannelsCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading: contextLoading, refresh } = useDocumentContext();
  const { locked } = useSimpleFormContext();
  const setPageData = useZustand('module', 'setPageData');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Determine the entity ID - supports both:
  // 1. Subscription context: data has entityId field pointing to linked entity
  // 2. Customers context: data IS the entity, so use _id directly
  const entityId = data?.entityId || data?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  // Controlled sort: contactType direction can change, but isPrimary is always 'asc' (first)
  const [sortOrder, setSortOrder] = useState('ascend');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handleAdd = useCallback(() => {
    openModal(ContactChannelModal, {
      entityId, // Use entity ID, not subscription ID
      initialData: null,
      onSuccess: async (updatedEntity, savedContact) => {
        // SAFETY: Ensure we have valid data before updating
        if (!data) return;

        // The _id is generated in the frontend (ContactChannelModal), so we can trust savedContact
        // Simply add the contact directly - no need to check API response
        const existingContacts = data?.contacts || [];

        // If this contact is primary, remove isPrimary from other contacts of same type
        let updatedContacts = existingContacts;
        if (savedContact?.isPrimary) {
          updatedContacts = existingContacts.map(c =>
            c.contactType === savedContact.contactType && c.isPrimary
              ? { ...c, isPrimary: false }
              : c
          );
        }

        // Avoid duplicates by checking if contact already exists
        const contactExists = updatedContacts.some(c => c._id === savedContact?._id);
        const newContacts = contactExists ? updatedContacts : [...updatedContacts, savedContact];

        setPageData({ data: { ...data, contacts: newContacts }, force: true });
      },
    });
  }, [data, entityId, setPageData]);

  const handleAddPerson = useCallback(() => {
    openModal(ContactPersonModal, {
      entityId,
      existingContacts: data?.contacts,
      onSuccess: async (savedEntity, savedContact) => {
        if (!data) return;

        // Enrich contact with _linkedEntity for immediate display
        const enrichedContact = {
          ...savedContact,
          _linkedEntity: savedEntity || null,
        };

        const existingContacts = data?.contacts || [];

        // If this person contact is primary, demote other person contacts
        let updatedContacts = existingContacts;
        if (enrichedContact.isPrimary) {
          updatedContacts = existingContacts.map(c =>
            c.contactType === 'person' && c.isPrimary ? { ...c, isPrimary: false } : c
          );
        }

        // Avoid duplicates by checking if contact already exists
        const contactExists = updatedContacts.some(c => c._id === enrichedContact?._id);
        const newContacts = contactExists ? updatedContacts : [...updatedContacts, enrichedContact];

        setPageData({ data: { ...data, contacts: newContacts }, force: true });
      },
    });
  }, [data, entityId, setPageData]);

  const handleEditPerson = useCallback(
    record => {
      const entityData = record._linkedEntity || null;

      openModal(EditPersonModal, {
        parentEntityId: entityId,
        contact: record,
        linkedEntity: entityData,
        onSuccess: async (freshEntity, updatedContact) => {
          if (!data) return;

          // Enrich contact with updated _linkedEntity
          const enrichedContact = {
            ...updatedContact,
            _linkedEntity: freshEntity || null,
          };

          // Update the contact in the local contacts array
          const existingContacts = data?.contacts || [];

          // If this person contact is now primary, demote other person contacts
          let updatedContacts = existingContacts;
          if (enrichedContact.isPrimary) {
            updatedContacts = existingContacts.map(c =>
              c.contactType === 'person' && c.isPrimary && c._id !== enrichedContact._id
                ? { ...c, isPrimary: false }
                : c
            );
          }

          const newContacts = updatedContacts.map(c =>
            c._id === enrichedContact._id ? { ...c, ...enrichedContact } : c
          );
          setPageData({ data: { ...data, contacts: newContacts }, force: true });
        },
      });
    },
    [data, entityId, setPageData]
  );

  const handleEdit = useCallback(
    record => {
      // Person contacts use dedicated edit modal
      if (record.contactType === 'person') {
        handleEditPerson(record);
        return;
      }

      openModal(ContactChannelModal, {
        entityId, // Use entity ID, not subscription ID
        initialData: record,
        onSuccess: async (updatedEntity, savedContact) => {
          // SAFETY: Ensure we have valid data before updating
          if (!data) return;

          // The _id is preserved from the original record, so we can trust savedContact
          const existingContacts = data?.contacts || [];

          // If this contact is now primary, remove isPrimary from other contacts of same type
          let updatedContacts = existingContacts;
          if (savedContact?.isPrimary) {
            updatedContacts = existingContacts.map(c =>
              c.contactType === savedContact.contactType &&
              c.isPrimary &&
              c._id !== savedContact._id
                ? { ...c, isPrimary: false }
                : c
            );
          }

          // Update the contact in the array
          const newContacts = updatedContacts.map(c =>
            c._id === savedContact?._id ? { ...c, ...savedContact } : c
          );

          setPageData({ data: { ...data, contacts: newContacts }, force: true });
        },
      });
    },
    [data, entityId, handleEditPerson, setPageData]
  );

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        // SAFETY: Ensure we have valid data before updating
        if (!data) return;

        // Remove contact via API (use entity ID, not subscription ID)
        await entitiesService.addToSet({
          _id: entityId,
          operator: '$pull',
          path: 'contacts',
          data: { subdocumentId: record._id },
        });

        // Remove contact directly from local array - no need to check API response
        const existingContacts = data?.contacts || [];
        const newContacts = existingContacts.filter(c => c._id !== record._id);

        setPageData({ data: { ...data, contacts: newContacts }, force: true });
        appService.message({ flag: 's', text: 'channel_removed' });
      } catch (error) {
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data, entityId, setPageData]
  );

  const handleTogglePrimary = useCallback(
    async record => {
      if (!data) return;

      const newIsPrimary = !record.isPrimary;

      setLoading(true);
      try {
        // Build the full contact data for in-place update (backend detects _id and updates)
        const contactData = {
          _id: record._id,
          contactType: record.contactType,
          value: record.value,
          countryCode: record.countryCode || 'BR',
          isPrimary: newIsPrimary,
          ...(record.phoneType && { phoneType: record.phoneType }),
          ...(record.note && { note: record.note }),
          ...(record.linkedEntityId && { linkedEntityId: record.linkedEntityId }),
        };

        await entitiesService.addToSet({
          _id: entityId,
          operator: '$push',
          path: 'contacts',
          data: contactData,
        });

        // Update local state: toggle this contact and demote others of same contactType
        const existingContacts = data?.contacts || [];
        const newContacts = existingContacts.map(c => {
          if (c._id === record._id) {
            return { ...c, isPrimary: newIsPrimary };
          }
          // If marking as primary, demote other contacts of same contactType
          if (newIsPrimary && c.contactType === record.contactType && c.isPrimary) {
            return { ...c, isPrimary: false };
          }
          return c;
        });

        setPageData({ data: { ...data, contacts: newContacts }, force: true });
      } catch (error) {
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [data, entityId, setPageData]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Get contacts from document data
  const contacts = useMemo(() => {
    return normalizeContacts(data?.contacts);
  }, [data?.contacts]);

  // Filter out removed contacts and sort: isPrimary ALWAYS first (intrinsic asc), then by channel group
  // The sortOrder state controls only the contactType direction, isPrimary is always ascending
  const visibleContacts = useMemo(() => {
    const isDescending = sortOrder === 'descend';
    return contacts
      .filter(c => !c.removed)
      .sort((a, b) => {
        // isPrimary is ALWAYS ascending (primary contacts first) - intrinsic behavior
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;

        // Then by channel group order (respects sortOrder direction)
        const groupA = CHANNEL_TYPE_CONFIG[a.contactType]?.group || 'other';
        const groupB = CHANNEL_TYPE_CONFIG[b.contactType]?.group || 'other';
        const orderA = CHANNEL_GROUP_ORDER[groupA] || 99;
        const orderB = CHANNEL_GROUP_ORDER[groupB] || 99;

        // Apply direction to contactType sort only
        const groupCompare = orderA - orderB;
        return isDescending ? -groupCompare : groupCompare;
      });
  }, [contacts, sortOrder]);

  // Handle table sort changes - only updates contactType direction, isPrimary stays 'asc'
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter?.field === 'contactType') {
      setSortOrder(sorter.order || 'ascend');
    }
  }, []);

  const addMenuItems = [
    {
      key: 'channel',
      icon: <AimOutlined />,
      label: translateX('contact_channel'),
      onClick: handleAdd,
    },
    {
      key: 'person',
      icon: <UserOutlined />,
      label: translateX('person'),
      onClick: handleAddPerson,
    },
  ];

  const isDisabled = loading || contextLoading || locked;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('channel'),
      dataIndex: 'contactType',
      width: 150,
      // Controlled sorter: sorting is handled in visibleContacts memo
      // isPrimary is always 'asc' (intrinsic), contactType follows sortOrder
      // sorter: true, // Enable sorter UI but actual sorting is controlled
      // sortOrder: sortOrder, // Controlled sort order
      render: (type, record) => {
        const config = CHANNEL_TYPE_CONFIG[type] || CHANNEL_TYPE_CONFIG.other;
        const label = config.label.includes(' ') ? config.label : translateX(config.label);

        return (
          <Space>
            <Tag color={config.color}>{label}</Tag>
            <Tooltip title={translateX('favorite')}>
              <span
                style={{ cursor: isDisabled ? 'default' : 'pointer', lineHeight: 1 }}
                onClick={() => !isDisabled && handleTogglePrimary(record)}
              >
                {record.isPrimary ? (
                  <StarFilled style={{ color: '#faad14' }} />
                ) : (
                  <StarOutlined style={{ color: '#d9d9d9' }} />
                )}
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: translateX('reference'),
      dataIndex: 'value',
      // Note: When sorting by reference, isPrimary order may change because this is not controlled
      // sorter: (a, b) => {
      //   const valA = (a.value || '').toLowerCase();
      //   const valB = (b.value || '').toLowerCase();
      //   return valA.localeCompare(valB);
      // },
      render: (value, record) => {
        // Person contacts: icon vertically centered + name, phone, email
        if (record.contactType === 'person') {
          const personData = record._linkedEntity;
          // Try contacts array first, then virtual fields (phone/email) as fallback
          const personPhone =
            personData?.contacts?.find(c => c.contactType === 'phone') || personData?.phone;
          const personEmail =
            personData?.contacts?.find(c => c.contactType === 'email') || personData?.email;
          const phoneDisplay = personPhone
            ? parsePhoneToDisplay(personPhone.value, personPhone.countryCode)
            : null;

          return (
            <Flex align="center" gap={8}>
              {/*<UserOutlined style={{ color: '#9c27b0', fontSize: 16 }} />*/}
              <Flex vertical gap={0}>
                <span className="font-weight-bold">{personData?.name || value}</span>
                {phoneDisplay && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    <PhoneOutlined style={{ marginRight: 4, fontSize: 11 }} />
                    {phoneDisplay}
                  </span>
                )}
                {personEmail && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    <MailOutlined style={{ marginRight: 4, fontSize: 11 }} />
                    {personEmail.value}
                  </span>
                )}
              </Flex>
            </Flex>
          );
        }

        // Format phone numbers for display (pass countryCode for proper formatting)
        const displayValue =
          record.contactType === 'phone' ? parsePhoneToDisplay(value, record.countryCode) : value;

        // Get label tag for phone type
        const phoneTypeTag =
          record.contactType === 'phone' && record.phoneType ? (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              {translateX(record.phoneType)}
            </Tag>
          ) : null;

        // WhatsApp icon for phone contacts
        const whatsAppIcon =
          record.contactType === 'phone' && record.hasWhatsApp ? (
            <Tooltip title="WhatsApp">
              <WhatsAppOutlined style={{ color: '#25D366', marginLeft: 4 }} />
            </Tooltip>
          ) : null;

        return (
          <Space>
            <span className="font-weight-bold">{displayValue}</span>
            {whatsAppIcon}
            {/*{phoneTypeTag}*/}
          </Space>
        );
      },
    },
    {
      title: translateX('note'),
      dataIndex: 'note',
      width: 200,
      render: note => (
        <span className="text-muted" style={{ fontSize: 12 }}>
          {note || '-'}
        </span>
      ),
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title={translateX('edit')}>
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              disabled={isDisabled}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={translateX('remove_this_channel')}
            onConfirm={() => handleDelete(record)}
            disabled={isDisabled}
          >
            <Tooltip title={translateX('remove')}>
              <Button
                size="small"
                type="text"
                icon={<RestOutlined />}
                disabled={isDisabled}
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // if (!data?._id || data?.entityType !== 'company') return null;
  if (!data?._id) return null;

  return (
    <Card
      title={<div className="text-muted">{translateX('contact_channels')}</div>}
      className="mt-3 no-shadow"
      extra={
        <Dropdown menu={{ items: addMenuItems }} trigger={['click']} disabled={isDisabled}>
          <Button icon={<PlusOutlined />} disabled={isDisabled} />
        </Dropdown>
      }
    >
      <SimpleTable
        columns={columns}
        // className={`no-border${isDisabled ? ' table-disabled' : ''}`}
        dataSource={visibleContacts.slice(0, 100)} // Limit to 100 rows, no pagination
        pagination={false}
        size="small"
        loading={loading}
        locale={{ emptyText: translateX('no_channels') }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EntityChannelsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeContacts(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeContact);
}

function normalizeContact(contact) {
  return {
    _id: contact._id || generateMongoObjectId(), // Generate fallback _id if missing
    contactType: contact.contactType || 'other',
    value: contact.value || '',
    phoneType: contact.phoneType,
    note: contact.note || '',
    isPrimary: contact.isPrimary || false,
    countryCode: contact.countryCode || 'BR',
    hasWhatsApp: contact.hasWhatsApp || false,
    ...(contact.linkedEntityId && { linkedEntityId: contact.linkedEntityId }),
    ...(contact._linkedEntity && { _linkedEntity: contact._linkedEntity }),
    ...(contact.removed && { removed: true }),
  };
}
